<template>
    <div
        class="overflow-hidden relative rounded flex justify-center items-center w-full"
        :class="[
            outlined
                ? 'before:inset-ring before:inset-ring-stone-500/35 before:absolute before:w-full before:h-full before:rounded before:left-0'
                : undefined,
            ui?.root,
        ]"
        :style="{ aspectRatio: ASPECT_RATIOS[aspectRatio] }"
    >
        <NuxtImg
            class="w-full"
            :class="ui?.img"
            :src="src"
            :srcset="srcset"
            :placeholder="src"
            :loading="loading"
            :alt="alt || ''"
        ></NuxtImg>
    </div>
</template>

<script setup lang="ts">
// DEFINE IMPORTS
import { ASPECT_RATIOS } from '@/data/aspect-ratios'
import type { CloudflareImage, CloudflareVariantSize } from '~~/layers/base/types/printingProcess'

// DECLARE TYPES
type AspectRatio = CloudflareImage['aspectRatio']

// PROPS DEFINITION
const props = withDefaults(
    defineProps<{
        outlined?: boolean
        imageId: string
        aspectRatio: AspectRatio
        sizeBreakpoints?: Record<string, CloudflareVariantSize>
        alt?: string
        loading?: 'lazy' | 'eager'
        /** Optional class overrides for the outer wrapper and inner NuxtImg. */
        ui?: {
            root?: string
            img?: string
        }
    }>(),
    {
        ui: undefined,
        sizeBreakpoints: () => ({
            150: 'thumb',
            200: 'xs',
            400: 'sm',
            800: 'md',
            1200: 'lg',
        }),
        loading: 'lazy',
    }
)

// SET TEMPLATE VARS
const src = (props.imageId, props.aspectRatio, 'thumb')
const srcset = Object.entries(props.sizeBreakpoints).map(
    ([viewportWidth, variantSize]) =>
        `${useCloudflareImgUrl(props.imageId, props.aspectRatio, variantSize)} ${viewportWidth}w`
)
</script>
